import type { FC } from "react";
import { useState } from "react";

import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";
import { BasicModal, Button } from "@similarweb/ui-components";

import { afterTrialBannerModalStyles } from "app/common/after-trial/afterTrialBannerModalStyles";
import { isAfterTrialCCFromQuiz } from "app/common/after-trial/afterTrialSwSettingsPredicates";
import { swSettings } from "app/common/services/swSettings";
import { AfterTrialPopupContainer } from "app/components/React/AfterTrialPopup/AfterTrialPopupContainer";
import { i18nFilter } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

const BannerWrapper = styled.div<{ isSI: boolean }>`
    background-color: ${colorsPalettes.carbon[0]};
    padding: 32px;
    box-shadow: 0 3px 6px ${rgba(colorsPalettes.carbon[500], 0.08)};
    border-radius: 6px;
    display: flex;
    justify-content: space-between;
    margin: ${({ isSI }) => (isSI ? "0" : " 0 40px")};
`;

const Title = styled.div`
    ${setFontVariant("h4", colorsPalettes.carbon[500])};
    margin-bottom: 12px;
`;

const SubTitle = styled.div`
    ${setFontVariant("report", colorsPalettes.carbon[400])};
    margin-bottom: 24px;
`;

const BulletContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    margin-bottom: 20px;
`;

const BulletText = styled.div`
    ${setFontVariant("input2", colorsPalettes.carbon[500])};
`;

const StyledButton = styled(Button)`
    align-self: flex-start;
`;

const ImgWrapper = styled.div`
    width: 190px;
    height: 190px;
    padding-top: 23px;
`;

const translate = i18nFilter();

export { afterTrialBannerModalStyles };

interface IAfterTrialBannerB {
    isSI?: boolean;
}

export const AfterTrialBannerB: FC<IAfterTrialBannerB> = ({ isSI }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const isSkipTrial = isAfterTrialCCFromQuiz(swSettings);

    const BulletsKeys = isSI
        ? [
              "aftertrial.homepage.banner.si.bullet1",
              "aftertrial.homepage.banner.si.bullet2",
              "aftertrial.homepage.banner.si.bullet3",
          ]
        : [
              "aftertrial.homepage.banner.bullet1",
              "aftertrial.homepage.banner.bullet2",
              "aftertrial.homepage.banner.bullet3",
          ];
    const getCTAButton = () => {
        return (
            <StyledButton
                style={{ lineHeight: "normal" }}
                onClick={() => {
                    allTrackers.trackEvent(
                        "Internal Link",
                        "click",
                        isSkipTrial ? "Reactivate CC Trial/homepage/banner" : "Start CC Trial/homepage/banner",
                    );
                    setIsOpen(true);
                }}
            >
                {translate(
                    isSkipTrial
                        ? "trial_banner.2.button.go.to.packages.skip.text"
                        : "trial_banner.2.button.go.to.packages.text",
                )}
            </StyledButton>
        );
    };

    return (
        <BannerWrapper isSI={isSI}>
            <FlexColumn>
                <div>
                    <Title>
                        {translate(
                            isSkipTrial
                                ? "aftertrial.homepage.banner.skip.title"
                                : "aftertrial.homepage.banner.b.title",
                        )}
                    </Title>
                    <SubTitle>
                        {translate(
                            isSkipTrial
                                ? "aftertrial.homepage.banner.skip.sub.title"
                                : "aftertrial.homepage.banner.b.sub.title",
                        )}
                    </SubTitle>
                    {BulletsKeys.map((bullet) => {
                        return (
                            <BulletContainer>
                                <SWReactIcons
                                    iconName={"checked"}
                                    size={"xs"}
                                    iconColor={colorsPalettes.blue[400]}
                                />
                                <BulletText>{translate(bullet)}</BulletText>
                            </BulletContainer>
                        );
                    })}
                </div>
                {getCTAButton()}
            </FlexColumn>
            <ImgWrapper>
                <img
                    alt=""
                    src={AssetsService.assetUrl("/images/after-trial-banner-2.png")}
                />
            </ImgWrapper>
            <BasicModal
                isOpen={isOpen}
                onRequestClose={() => setIsOpen(false)}
                closeIconConfig={{ show: false }}
                customStyles={afterTrialBannerModalStyles}
                shouldCloseOnOverlayClick={true}
            >
                <AfterTrialPopupContainer
                    setIsOpen={setIsOpen}
                    isSI={isSI}
                />
            </BasicModal>
        </BannerWrapper>
    );
};
